import { useEffect, useMemo, useRef, useState } from "react";
import TranslateBorderedIcon from "../../icons/translate-bordered.icon";
import MyDropdown from "../my-dropdown/my-dropdown.component";
import './ai-toolbar.styles.scss';
import useAIActions from "../../../hooks/actions/ai.actions";
import SuccessSignIcon from "../../icons/success-sign.icon";
import { Spinner } from "react-bootstrap";
import MagicBorderedIcon from "../../icons/magic-bordered.icon";
import { AI_IMPROVEMENT_LIST, AI_STYLE_LIST, AI_TRANSLATE_LANGUAGES } from "../../../constants/ai-tools.constants";
import SmallChevronIcon from "../../icons/small-chevron.icon";
import ToneBorderedIcon from "../../icons/tone-bordered.icon";
import aiUtils from "../../../Utils/ai.utils";

const AIToolbar = ({ inputRef, onUpdate = () => { }, inputValue = '', maxLength, passConfig = true }) => {
  const toolbarRef = useRef(null);
  const [curDropdown, setCurDropdown] = useState(null);
  const { aiAssistant } = useAIActions();

  const tools = [
    {
      key: 0,
      label: 'Improve',
      Icon: MagicBorderedIcon,
      options: AI_IMPROVEMENT_LIST
    },
    {
      key: 1,
      label: 'Translate to',
      Icon: TranslateBorderedIcon,
      isLanguageMenu: true,
      options: AI_TRANSLATE_LANGUAGES
    },
    {
      key: 2,
      label: 'Tone of voice',
      Icon: ToneBorderedIcon,
      options: AI_STYLE_LIST
    },
  ]
  const handleAction = ({
    prompt, fromLanguage, toLanguage, option, setSelectedOption
  }) => {
    const selection = window.getSelection().toString();
    let text = inputValue != '' ? inputValue : selection || inputRef?.current?.value
    setSelectedOption({ ...option, loading: true });
    const createdPrompt = prompt
      .replace('[text]', text)
      .replace('[fromLanguage]', fromLanguage ? `from ${fromLanguage}` : '')
      .replace('[toLanguage]', toLanguage ? toLanguage : '');
    aiAssistant({ prompt: `${maxLength && `Keep it under ${maxLength} characters. Just return the revised sentence no longer than ${maxLength} characters total, including spaces and punctuation. `}${createdPrompt}` })
      .then((response) => {
        setSelectedOption({ ...option, loading: false });
        if (inputValue != '') {
          onUpdate({ message: response?.message });
        } else if (selection) {
          document.execCommand('insertText', false, response?.message);
        } else {
          inputRef.current.value = response?.message;
          if (inputRef.current.editor) {
            inputRef.current.editor.setText(response?.message);
          }
          onUpdate({ message: response?.message });
        }
        document.body.click();
      })
      .catch((error) => {
        console.log(error);
        setSelectedOption(null);
      });
  }

  useEffect(() => {
    const preventDeselect = (event) => {
      if (toolbarRef.current && toolbarRef.current.contains(event.target)) {
        event.preventDefault();
      }
    };
    document.addEventListener("mousedown", preventDeselect, true);

    return () => {
      document.removeEventListener("mousedown", preventDeselect, true);
    };
  }, []);

  return (
    <div ref={toolbarRef} className="ai-toolbar-container">
      {tools.map((tool) => (
        <ToolItem
          key={`ai-toolbar-${tool.key}`}
          tool={tool}
          curDropdown={curDropdown}
          setCurDropdown={setCurDropdown}
          inputRef={inputRef}
          handleAction={handleAction}
          inputValue={inputValue}
          passConfig={passConfig}
        />
      ))}
    </div>
  )
}

export default AIToolbar;

const ToolItem = ({ tool, curDropdown, setCurDropdown, inputRef, handleAction, inputValue = '', passConfig = true }) => {
  const refBtn = useRef(null);
  const [definedLanguage, setDefinedLanguage] = useState('');
  const { aiAssistant, isAssistantLoading } = useAIActions();
  const [selectedOption, setSelectedOption] = useState(null);

  const isOpen = curDropdown?.tool?.key === tool.key && curDropdown?.isOpen;

  const defineLanguage = () => {
    let text = inputValue != '' ? inputValue : window.getSelection().toString() || inputRef?.current?.value
    if (!text) return;

    const createdPrompt = aiUtils.promptTextLanguage(text);
    aiAssistant({ prompt: createdPrompt })
      .then((response) => {
        setDefinedLanguage(response?.message);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  useEffect(() => {
    if (!isOpen) {
      setDefinedLanguage('');
      return;
    }
    if (!tool.isLanguageMenu) return;
    defineLanguage();
  }, [isOpen]);

  return (
    <MyDropdown
      refBtn={refBtn}
      setRefBtn={(ref) => refBtn.current = ref?.current}
      key={`ai-toolbar-${tool.key}`}
      className="d-flex"
      toggleClassName="d-flex"
      toggleComponent={(
        <div className={`toggle-wrapper ${isOpen ? 'open' : ''}`}>
          <div className="icon-wrapper">
            <tool.Icon className="f-grey" />
          </div>
          {isOpen && (
            <div className="f-12 line-height-18 f-grey">
              {tool.label}
            </div>
          )}
          <div className="chevron-wrapper">
            <SmallChevronIcon className={`f-grey ${isOpen ? 'rotate180' : ''}`} width="7px" height="7px" />
          </div>
        </div>
      )}
      onToggle={(isOpen) => {
        setCurDropdown({ tool, isOpen });
      }}
      popperConfig={passConfig && { strategy: 'fixed' }}
      menuClassName="max-width-220"
      renderOnMount
    >
      <div>
        {tool?.isLanguageMenu && (
          <div className="d-flex flex-column gap-2">
            <LanguageDropdown
              options={tool?.options}
              isLangDefining={isAssistantLoading}
              definedLanguage={definedLanguage}
              onSelectOption={(option) => {
                setDefinedLanguage(option.label);
              }}
              inputRef={inputRef}
              inputValue={inputValue}
            />
            <div className="separator w-100 mx-3" />
            <span
              className="line-height-12 f-darkgrey f-500 translate-to-container"
            >
              Translate to
            </span>
          </div>
        )}
        <div className="max-height-300 overflow-y-auto hide-scrollbar d-flex flex-column gap-2">
          {tool?.options?.map((option) => (
            <MyDropdown.Item
              key={`ai-toolbar-${tool.key}-${option.key}`} className="f-grey d-flex align-items-center justify-content-between gap-8"
              style={{
                ...(tool?.isLanguageMenu && {
                  ...((!definedLanguage || option.label === definedLanguage) && { opacity: 0.5, pointerEvents: 'none' })
                })
              }}
              onClick={() => handleAction({
                prompt: option.prompt,
                fromLanguage: definedLanguage,
                toLanguage: option.label,
                option,
                setSelectedOption
              })}
              closeOnClick={false}
              disabled={inputValue != '' ? inputValue == '' : !inputRef?.current?.value}
            >
              <div className="d-flex align-items-center gap-8">
                {!selectedOption?.loading && selectedOption?.key === option.key && (
                  <SuccessSignIcon width={12} height={9} fill={"#616778"} />
                )}
                <span>{option.label}</span>
              </div>
              {selectedOption?.loading && selectedOption?.key === option.key && (
                <Spinner
                  animation="border"
                  variant="secondary"
                  size="sm"
                  style={{
                    width: "12px",
                    height: "12px",
                  }}
                />
              )}
            </MyDropdown.Item>
          ))}
        </div>
      </div>
    </MyDropdown>
  )
}

const LanguageDropdown = ({
  options,
  isLangDefining,
  definedLanguage,
  inputRef,
  onSelectOption = () => { },
  inputValue = ''
}) => {
  const filteredOptions = useMemo(() => {
    return options?.filter((option) => option.label !== definedLanguage);
  }, [options, definedLanguage]);
  return (
    <MyDropdown
      toggleClassName="w-100"
      toggleComponent={(
        <div
          className="item f-grey w-100 d-flex align-items-center justify-content-between overflow-hidden text-ellipsis"
          style={{ padding: '4px 10px' }}
        >
          <div>
            <span>
              {`From: `}
            </span>
            <span>
              {isLangDefining ? 'Defining...' : definedLanguage
                ? definedLanguage
                : 'Select language'}
            </span>
          </div>
          <SmallChevronIcon className="rotate270 mr-3" width="8px" height="8px" />
        </div>
      )}
      drop="right"
      menuClassName="max-height-300 max-width-180 overflow-y-auto hide-scrollbar"
      popperConfig={{ strategy: 'fixed' }}
      renderOnMount
    >
      {definedLanguage && (
        <>
          <div className="f-grey d-flex align-items-center gap-8" style={{ padding: '4px 12px' }}>
            <SuccessSignIcon width={12} height={9} fill={"#616778"} />
            <span>
              {definedLanguage}
            </span>
          </div>
          <div className="separator w-100 mx-3" />
        </>
      )}
      {filteredOptions?.map((option) => (
        <MyDropdown.Item
          key={`ai-toolbar-lang-${option.key}`}
          className="f-grey"
          onClick={() => onSelectOption(option)}
          disabled={inputValue != '' ? inputValue == '' : !inputRef?.current?.value}
        >
          {option.label}
        </MyDropdown.Item>
      ))}
    </MyDropdown>
  )
}